import { Box, ButtonBase, Popover, Typography } from '@mui/material';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import { bool, func, node, object, shape, string } from 'prop-types';
import React from 'react';

const IdentityCard = ({
  icon,
  title,
  slug = null,
  pill = null,
  description = null,
  emptyDescription = null,
  edit = null,
  actions = null,
  divider = false,
  bareIcon = false,
  className = null,
}) => {
  const showInlineDescription = Boolean(description || emptyDescription);

  const titleNode = edit ? (
    <ButtonBase
      onClick={edit.onOpen}
      aria-expanded={edit.open}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        p: '0 6px',
        borderRadius: 1,
        border: '1px solid',
        borderColor: edit.open ? 'text.primary' : 'transparent',
        color: 'text.primary',
        maxWidth: '100%',
        backgroundColor: edit.open ? 'background.paper' : 'transparent',
        '&:hover': { backgroundColor: 'action.hover' },
        ml: -1.25,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: theme => theme.typography.pxToRem(20),
          fontWeight: 700,
          letterSpacing: '-0.4px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </Typography>
      <ChevronDownIcon size={18} />
    </ButtonBase>
  ) : (
    <Typography
      sx={{
        fontSize: theme => theme.typography.pxToRem(20),
        fontWeight: 700,
        color: 'text.primary',
        letterSpacing: '-0.4px',
      }}
    >
      {title}
    </Typography>
  );

  return (
    <Box
      className={className || undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        ...(divider && {
          borderBottom: 1,
          borderColor: 'divider',
          pb: 2.5,
        }),
      }}
    >
      {bareIcon ? (
        icon
      ) : (
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 1,
            borderColor: 'divider',
            backgroundColor: 'background.default',
            color: 'text.primary',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {titleNode}
          {slug && (
            <Typography
              sx={{
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: theme => theme.typography.pxToRem(12),
                color: 'text.secondary',
              }}
            >
              · {slug}
            </Typography>
          )}
          {pill}
        </Box>
        {showInlineDescription &&
          (description ? (
            <Typography
              sx={{
                fontSize: theme => theme.typography.pxToRem(13),
                color: 'text.secondary',
                lineHeight: 1.5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {description}
            </Typography>
          ) : (
            <Typography
              sx={{
                fontSize: theme => theme.typography.pxToRem(13),
                color: 'text.secondary',
                lineHeight: 1.5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontStyle: 'italic',
              }}
            >
              {emptyDescription}
            </Typography>
          ))}
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>{actions}</Box>
      )}
      {edit && (
        <Popover
          open={edit.open}
          anchorEl={edit.anchorEl}
          onClose={edit.onClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <Box
            sx={{
              p: 2,
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {edit.content}
          </Box>
        </Popover>
      )}
    </Box>
  );
};

IdentityCard.propTypes = {
  icon: node.isRequired,
  title: node.isRequired,
  slug: node,
  pill: node,
  description: node,
  emptyDescription: node,
  edit: shape({
    open: bool.isRequired,
    anchorEl: object,
    onOpen: func.isRequired,
    onClose: func.isRequired,
    content: node.isRequired,
  }),
  actions: node,
  divider: bool,
  bareIcon: bool,
  className: string,
};

export default IdentityCard;
